/**
 * The CATALOGUE half of the admin surface: the product master and per-country pricing.
 *
 * ===========================================================================
 * A THIRD FILE, FOR THE REASON `infra-api.ts` IS A SECOND ONE
 * ===========================================================================
 * `infra-api.ts` records why the generic name `api.ts` is deliberately left free: one
 * owner per file, because two work streams writing the same filename in the same week is
 * a merge conflict by appointment. This is the catalogue stream, so it gets its own.
 *
 * ===========================================================================
 * ⚠ `CountryProductDto` IS ALSO TRANSCRIBED IN `features/quotes/api.ts`
 * ===========================================================================
 * And that is a real hazard rather than a tidy re-use, so it is named here: the quotes
 * copy exists for the BUILDER's line-item picker and that copy has already gone stale
 * once, silently, when migration 17 added four fields. It type-checked the whole time.
 *
 * This module deliberately does NOT import it. Two screens with different jobs reading
 * one hand-maintained interface means a field added for one can quietly change the other,
 * and the compiler cannot tell you which screens actually needed it. What protects both
 * is the same thing: `catalogue.test.tsx` and `quotes-builder.test.tsx` each build a
 * fixture of their own shape, so a server change that breaks either fails loudly in that
 * screen's own suite.
 *
 * Every call goes through `apiRequest`, which owns the single-flight refresh mutex
 * (D-web-1). ESLint forbids `fetch` outside `src/lib/api.ts` for exactly that reason.
 */

import { apiRequest } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/* ─────────────────────────────────────────────────────── the product master ── */

/**
 * `ProductDto` — transcribed from
 * `apps/api/src/modules/catalog-stub/products.service.ts:56`.
 *
 * ⚠ THIS SAID "GLOBAL, not country-scoped: `products` has no `country_id`" AND EVERY WORD
 * OF THAT IS NOW FALSE. Migration 22 gave the table a `country_id` and the owner ruled with
 * it: each Country Admin creates, edits and prices their OWN products. The writes carry
 * `config.manage` IN THAT COUNTRY (`catalog.controller.ts`), not `config.global`, and
 * Angola's "TallyPrime Silver" and India's are separate rows.
 *
 * The stale sentence is quoted rather than deleted because it is the reason the product
 * master screen was gated on `isGroupAdmin` — the drift was believed, twice, in two files.
 */
export interface ProductDto {
  id: string;
  /**
   * The country that OWNS this row (migration 22) — transcribed from the API's own
   * `ProductDto` (`products.service.ts`), which has sent it since the split for exactly
   * the reason the merged screen now reads it: `code` is only unique per country, so
   * the group admin's interleaved list is unreadable without a per-row country. The two
   * predecessor screens never read it and this transcription had silently omitted it —
   * the field is not new on the wire, only newly transcribed.
   */
  country_id: number;
  code: string;
  name: string;
  category: string | null;
  /**
   * The B2 classification — see {@link PRODUCT_KINDS}.
   *
   * ⚠ THIS SAID *"read-only over HTTP today"* AND THAT WAS FALSE WHEN IT WAS WRITTEN. The
   * API has accepted `kind` on `PATCH /products/:id` since the per-country ruling, gated
   * `grp:true`; what was missing was any client able to send it — this file's
   * `UpdateProductBody` had no such field, and mobile has no catalogue admin at all. That
   * is defect **D-3**, and the sentence is quoted rather than deleted because believing it
   * is exactly what kept the AO/CD government patch undesignatable through either UI.
   *
   * Still typed `string` on the READ, deliberately: a server that grows a sixth class must
   * not make this screen render nothing. The WRITE is typed — see `UpdateProductBody`.
   */
  kind: string;
  pricing_model: string;
  meta: Record<string, unknown> | null;
  active: boolean;
  /**
   * The product's NAME awaits confirmation (migration 15) — distinct from the per-country
   * price flag of the same name. The NAME flag still gates documents (NAME_UNVERIFIED,
   * OD-4) and is counted by `/health`; the price flag is informational since G1.
   */
  verify_before_prod: boolean;
  /**
   * Sold on a fixed TERM, so its quote lines carry a renewal term and an expiry
   * (migration 18).
   *
   * CATALOGUE DATA, which is the whole point: this screen is how a product becomes
   * term-bearing, with no code change and no deploy. THREE are seeded true — TSS and the
   * two editions the owner added, TSS-SILVER and TSS-GOLD. The edition upgrade is NOT one
   * of them: a one-time licence change renews nothing, and marking it term-bearing would
   * quietly attach a term and an expiry to a one-off fee.
   */
  term_bearing: boolean;
  /**
   * The shelf this product is filed on, or `null` for Ungrouped — U3, migration 23.
   *
   * BOTH FIELDS, because the server sends both and the reason it does is this screen: the
   * id is what a write sends back, the NAME is what a heading shows. Deriving the name from
   * a separately-fetched group list would be a client-side join, and a client that joins is
   * a client that can disagree with the server about what a customer document says.
   *
   * `null` is ORDINARY. A country that has not decided how to file its catalogue has no
   * groups at all, and its screen must look exactly as it did before groups existed.
   */
  product_group_id: string | null;
  product_group_name: string | null;
}

export interface PagedProducts {
  data: ProductDto[];
  page: number;
  perPage: number;
  total: number;
}

/**
 * ONE COUNTRY'S catalogue — `?country=` is not an optional narrowing, it is the request.
 *
 * ⚠ THIS USED TO ASK FOR `/products?perPage=200` WITH NO COUNTRY AT ALL, and after
 * migration 22 that is a different question from the one the screen asks. The route scopes
 * an un-narrowed list to EVERY country the caller can stand in, so a two-country
 * Coordinator saw both catalogues interleaved and a group admin saw all six — five rows
 * called TP-SILVER with nothing on the screen to say which country each belonged to. The
 * screen is mounted under `/countries/:cc/`; the list it shows must be that country's.
 *
 * Asking for a country outside the caller's scope is `COUNTRY_SCOPE_VIOLATION`, not a
 * silently empty page (`products.service.ts` `list`) — so the refusal reaches the screen as
 * a refusal.
 */
export async function fetchProducts(
  cc: CountryCode,
  signal?: AbortSignal,
): Promise<PagedProducts> {
  // perPage is the API's ceiling; one country's catalogue is a few dozen rows and this
  // screen is a maintenance surface, so paging it would add a control nobody would use.
  return apiRequest<PagedProducts>(`/products?country=${cc}&perPage=200`, { signal });
}

/**
 * EVERY country the caller can stand in, interleaved — the GROUP ADMIN's view of the one
 * product screen (roles/products design §2.1).
 *
 * The un-narrowed request the warning above describes is exactly what this asks for, ON
 * PURPOSE and only from the merged screen's all-countries view: the server orders
 * country-first (`products.service.ts` `list`) and every row carries `country_id`, so the
 * screen can badge each row with its country instead of guessing. A non-grp seat never
 * issues this — the screen narrows them to the active country through `fetchProducts`.
 */
export async function fetchAllProducts(signal?: AbortSignal): Promise<PagedProducts> {
  return apiRequest<PagedProducts>('/products?perPage=200', { signal });
}

/**
 * The API's own ceiling — `MAX_PER_PAGE` in `modules/countries/support/http.ts`. Asking
 * for more is silently clamped to this, which is exactly how a caller ends up believing
 * it read everything.
 */
export const PRODUCTS_MAX_PER_PAGE = 200;

/** A stop, so a server that never advances `total` cannot spin the browser for ever. */
const REVIEW_PAGE_LIMIT = 25;

/**
 * EVERY page of the catalogue, for the GO-LIVE CHECKLIST — U4.
 *
 * ===========================================================================
 * WHY THIS EXISTS AND `fetchAllProducts` DOES NOT SERVE
 * ===========================================================================
 * Both ask the same route. The difference is what a SHORT answer means to each caller.
 * The products screen is a maintenance list: page one of a large catalogue is still a
 * useful screen. The checklist makes a CLAIM — "these are the rows behind /health's
 * number" — and a truncated first page turns that claim into "nothing is flagged" while
 * a flagged row sits on page two. That is the exact failure the restored screen exists
 * to end, so the read that feeds it must cover the whole catalogue or say it did not.
 *
 * `complete` is therefore returned rather than assumed: it is `false` when the server's
 * `total` was never reached (the page limit above, or a list that grew between pages),
 * and the screen renders that as a disclosure instead of an all-clear. Today's seeded
 * catalogue is a few dozen rows and this makes exactly ONE request — the loop is
 * insurance against a database nobody has met yet, not a cost paid now.
 *
 * `cc === null` is the group admin's un-narrowed read (`/products` with no `country`),
 * whose scope is every country — the only scope that can be compared with `/health`.
 * A country code narrows to that country, exactly as `fetchProducts` does.
 */
export interface ProductsForReview {
  rows: ProductDto[];
  /** The server's own count for the same question, from the last page read. */
  total: number;
  /** How many requests it took — 1 for any catalogue that fits a page. */
  pages: number;
  /** FALSE when `rows` is short of `total`: the screen must not claim completeness. */
  complete: boolean;
}

export async function fetchProductsForReview(
  cc: CountryCode | null,
  signal?: AbortSignal,
): Promise<ProductsForReview> {
  const rows: ProductDto[] = [];
  let total = 0;
  let pages = 0;

  for (let page = 1; page <= REVIEW_PAGE_LIMIT; page += 1) {
    const scope = cc === null ? '' : `country=${cc}&`;
    const body = await apiRequest<PagedProducts>(
      `/products?${scope}perPage=${String(PRODUCTS_MAX_PER_PAGE)}&page=${String(page)}`,
      { signal },
    );
    pages = page;
    total = body.total;
    rows.push(...body.data);
    // An empty page ends the read even if `total` disagrees — a server that keeps
    // promising rows it will not send must not become an infinite loop here.
    if (body.data.length === 0 || rows.length >= total) break;
  }

  return { rows, total, pages, complete: rows.length >= total };
}

/**
 * Seeded country id → code, transcribed from `@crf/shared` `COUNTRY_IDS` (types.ts) —
 * pinned ids, never auto-increment (`seed-countries.ts`).
 *
 * TRANSCRIBED RATHER THAN VALUE-IMPORTED: a VALUE import from `@crf/shared` typechecks
 * against the package SOURCE and then fails in rollup against the built CommonJS `dist`
 * (the WORK_BOARD_COLUMNS incident, recorded in `scripts/verify.sh`'s build step). The
 * cross-country list is the first web surface that must turn a DTO's `country_id` back
 * into a code — `CountryBadge`'s own header anticipates exactly this call-site change.
 *
 * ⚠ A HAND COPY IS ONLY AS GOOD AS ITS PIN, and this one decides which COUNTRY a price
 * write lands in (`beginPrice` → `putPrices` → `PUT /countries/:cc/products`), so drift
 * here files a price against the wrong catalogue without any refusal. `products-page.test.tsx`
 * holds it against `COUNTRY_IDS` itself — a TEST may value-import freely, so the drift
 * reddens there instead.
 */
export const COUNTRY_CODE_BY_ID: Readonly<Record<number, CountryCode>> = {
  1: 'IN',
  2: 'AE',
  3: 'TZ',
  4: 'AO',
  5: 'CD',
  6: 'XM',
};

/**
 * `POST /products` — the create the web never had (roles/products design §0.1: the API
 * has existed with tests since the per-country split; the screen simply never grew a
 * form — its own empty state said "add one through the API").
 *
 * ⚠ NO `kind` AND NO `verify_before_prod`, STRUCTURALLY. The server forces
 * `kind: 'unclassified'` (was `'crf_service'` before migration 46) and
 * `verify_before_prod: true` on every create (`products.service.ts`), and
 * `forbidNonWhitelisted` refuses a body naming `kind` at all. This interface deliberately
 * cannot express either field, so the create form cannot reopen the single-request hole
 * the design closed: creating a product and confirming its name are TWO acts (T-P1), and
 * the second one lives on the row. Since migration 46 there are THREE: classifying it is
 * its own act, on `UpdateProductBody.kind`, and it gates the second.
 */
export interface CreateProductBody {
  country: CountryCode;
  code: string;
  name: string;
  term_bearing?: boolean;
  product_group_id?: string | null;
}

export async function createProduct(body: CreateProductBody): Promise<ProductDto> {
  return apiRequest<ProductDto>('/products', { method: 'POST', body });
}

/**
 * The B2 classification vocabulary — TRANSCRIBED from `PRODUCT_KINDS` in
 * `apps/api/src/entities/product.entity.ts` (migration 46).
 *
 * ⚠ A HAND COPY THAT CANNOT BE PINNED, AND THAT IS SAID RATHER THAN HIDDEN. `COUNTRY_IDS`
 * lives in `@crf/shared` so `products-page.test.tsx` holds `COUNTRY_CODE_BY_ID` against the
 * real thing; `PRODUCT_KINDS` lives in an API entity the web cannot import at all, in
 * either direction. So there is no test that can redden on drift here.
 *
 * WHAT MAKES THAT TOLERABLE IS THE DIRECTION OF THE FAILURE, which is why the values are
 * used for a `<select>`'s OPTIONS and nothing else. A member added on the server and
 * missing here is an option an operator cannot pick — visible, and the row still RENDERS
 * its class, because `ProductDto.kind` is deliberately `string`. A member removed on the
 * server and left here is an option that answers 422, rendered verbatim. Neither silently
 * writes the wrong class, which is the only failure that would matter on this field.
 *
 * `unclassified` IS OFFERED. It is a legal value the server accepts, and hiding it would
 * mean a group admin who classified a row by mistake could never put it back — the screen
 * would be refusing something the server allows, which is the drift this file's header
 * warns about in the other direction.
 */
export const PRODUCT_KINDS = [
  'unclassified',
  'crf_service',
  'tally_licence',
  'gov_patch',
  'project_maintenance',
] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

/** What each class is called on the screen. A vocabulary an operator has to read. */
export const PRODUCT_KIND_LABEL: Readonly<Record<ProductKind, string>> = {
  unclassified: 'Not classified',
  crf_service: 'Service',
  tally_licence: 'Tally licence',
  gov_patch: 'Government patch',
  project_maintenance: 'Project maintenance',
};

export interface UpdateProductBody {
  name?: string;
  category?: string | null;
  active?: boolean;
  term_bearing?: boolean;
  verify_before_prod?: boolean;
  /**
   * The B2 class — **`grp:true` only**, and the missing half of defect D-3.
   *
   * The server has accepted this on `PATCH /products/:id` since the per-country ruling and
   * refuses it with `PERMISSION_DENIED` for anyone but a group admin
   * (`products.service.ts` `assertMayReclassify`); no shipped client could send it, so
   * outside the seeds every product in a live database kept whatever the create default
   * was — and the AO/CD government patch, whose whole job is to satisfy a licensing
   * condition, could not be designated at all.
   *
   * ⚠ THE SCREEN RENDERS THE CONTROL FOR A GROUP ADMIN ONLY. Not because the client is a
   * security boundary — it is not, and the server refuses regardless — but because a
   * control a Country Admin can see and cannot use is a worse screen than no control: it
   * teaches the seat that classification is theirs and then answers 403.
   */
  kind?: ProductKind;
  /**
   * Re-file the product; `null` clears it back to Ungrouped.
   *
   * ⚠ `null` IS A REQUEST AND MUST REACH THE WIRE. The API reads
   * `dto.product_group_id !== undefined`, so a client that dropped nulls from its body
   * would make "unfile this product" indistinguishable from "leave it alone" — and the
   * only way to unfile anything would be to file it somewhere else first.
   */
  product_group_id?: string | null;
}

export async function updateProduct(
  id: string,
  body: UpdateProductBody,
): Promise<ProductDto> {
  return apiRequest<ProductDto>(`/products/${id}`, { method: 'PATCH', body });
}

/* ────────────────────────────────────────────────────────── product groups ── */

/**
 * `ProductGroupDto` — transcribed from
 * `apps/api/src/modules/catalog-stub/product-groups.service.ts`.
 *
 * A shelf. Owner's example: Angola files E-Invoicing setup under "Addon"; UAE files its own
 * under "Item". Per country, like Tally's stock groups.
 *
 * ⚠ PURELY ORGANISATIONAL. Nothing here reaches a rule — not the patch requirement, not
 * tax, not pricing. The server measures that claim in `product-groups.e2e-spec.ts` rather
 * than asserting it, and this screen must not become the first place a group means anything.
 */
export interface ProductGroupDto {
  id: string;
  country_id: number;
  /** The top-level shelf this one sits under, or `null`. Groups nest ONE level. */
  parent_id: string | null;
  name: string;
  sort_order: number;
  active: boolean;
  /**
   * How many products are filed here right now.
   *
   * ON THE WIRE SO THE SCREEN CAN SAY SO BEFORE ANYBODY CLICKS. Deleting a group that still
   * holds products is refused, and a delete button that can only ever 409 is the same
   * defect as the "Confirm the name" button that could only ever 422.
   */
  product_count: number;
}

export async function fetchProductGroups(
  cc: CountryCode,
  signal?: AbortSignal,
): Promise<ProductGroupDto[]> {
  // `includeInactive` — this is the MAINTENANCE surface, so a retired shelf must still be
  // visible and re-openable here. The quote picker asks the same route without the flag.
  const body = await apiRequest<{ data: ProductGroupDto[] }>(
    `/product-groups?country=${cc}&includeInactive=true`,
    { signal },
  );
  return body.data;
}

export interface CreateProductGroupBody {
  country: CountryCode;
  name: string;
  parent_id?: string | null;
  sort_order?: number;
  active?: boolean;
}

export async function createProductGroup(
  body: CreateProductGroupBody,
): Promise<ProductGroupDto> {
  return apiRequest<ProductGroupDto>('/product-groups', { method: 'POST', body });
}

export interface UpdateProductGroupBody {
  name?: string;
  parent_id?: string | null;
  sort_order?: number;
  active?: boolean;
}

export async function updateProductGroup(
  id: string,
  body: UpdateProductGroupBody,
): Promise<ProductGroupDto> {
  return apiRequest<ProductGroupDto>(`/product-groups/${id}`, { method: 'PATCH', body });
}

export async function deleteProductGroup(
  id: string,
): Promise<{ deleted: true; id: string }> {
  return apiRequest<{ deleted: true; id: string }>(`/product-groups/${id}`, {
    method: 'DELETE',
  });
}

/* ────────────────────────────────────────────────────── per-country pricing ── */

/** `'past' | 'current' | 'future'` — mirrors the API's `PriceWindowState`. */
export const PRICE_WINDOW_STATES = ['past', 'current', 'future'] as const;
export type PriceWindowState = (typeof PRICE_WINDOW_STATES)[number];

/**
 * `CountryProductDto` — transcribed from
 * `apps/api/src/modules/catalog-stub/country-products.service.ts`.
 *
 * Since migration 17 a price is a VERSION, not a row: the key is
 * (country, product, effective_from) and the table holds a country's whole history.
 *
 * Since migration 66 (2026-09-22) the key also carries the CURRENCY: a country keeps one
 * fixed list per currency it quotes in (Congo: CDF and USD), and a price may be BLANK —
 * priced per customer, typed on every quote (owner ruling R3).
 */
export interface CountryProductDto {
  country_id: number;
  product_id: string;
  product_code: string;
  product_name: string;
  kind: string;
  term_bearing: boolean;
  /** THIS PRICE awaits confirmation. Per VERSION — a new price is unconfirmed even when
   * the one it replaces was confirmed years ago. */
  verify_before_prod: boolean;
  /**
   * DECIMAL(14,2) as a string, or the mask marker for a seat without `amounts.view` — or
   * `null` when {@link priced_per_customer}.
   */
  price: string | null;
  /** The list's price is deliberately blank: typed per quote (migration 66, R3). Never masked. */
  priced_per_customer: boolean;
  /** Which of the country's lists this row is in. */
  currency: string;
  /** FALSE means withdrawn IN THIS COUNTRY — which is not the same as unpriced. */
  active: boolean;
  effective_from: string;
  effective_to: string | null;
  /** Another version of this PRODUCT starts after the resolution date. */
  has_future_price: boolean;
  /** Where THIS ROW's window sits. `list` only ever returns `current`. */
  window_state: PriceWindowState;
}

export async function fetchCountryPrices(
  cc: CountryCode,
  signal?: AbortSignal,
): Promise<CountryProductDto[]> {
  const body = await apiRequest<{ data: CountryProductDto[] }>(
    `/countries/${cc}/products?includeInactive=true`,
    { signal },
  );
  return body.data;
}

/**
 * Every version, newest window first — `config.manage`.
 *
 * THE SCREEN IS BUILT ON THIS, not on the list. `GET /countries/:cc/products` resolves to
 * ONE row per product and OMITS any product whose versions do not cover today, so two of
 * the four states the domain distinguishes — "no price on that date" and a queued future
 * price — are literally invisible there. A maintenance screen that cannot see a gap
 * cannot be used to fix one.
 */
export async function fetchPriceHistory(
  cc: CountryCode,
  signal?: AbortSignal,
): Promise<CountryProductDto[]> {
  const body = await apiRequest<{ data: CountryProductDto[] }>(
    `/countries/${cc}/products/history`,
    { signal },
  );
  return body.data;
}

/** One item of the PUT body — `CountryProductEntryDto` in the API's catalogue DTO. */
export interface PriceEntry {
  product_id: string;
  /**
   * DECIMAL(14,2) as a string. Over-scale is REFUSED by the API, never rounded. `null` is the
   * explicit BLANK — priced per customer (migration 66, R3); the key is never omitted.
   */
  price: string | null;
  /** Which list — one of the country's quote currencies; the API refuses any other. */
  currency: string;
  active?: boolean;
  /**
   * First day this price applies. OMITTED means today in the COUNTRY's timezone —
   * resolved server-side, so a browser with a wrong clock cannot date a price.
   */
  effective_from?: string;
  /** Clears (or sets) "this price is unconfirmed". */
  verify_before_prod?: boolean;
}

export interface PutPricesResult {
  country_id: number;
  created: number;
  updated: number;
  unchanged: number;
  /** Today's list. ⚠ CAN OMIT a row this request just wrote — see {@link written}. */
  data: CountryProductDto[];
  /**
   * The rows actually written, each at its OWN effective date.
   *
   * The screen renders THIS after a save, never `data`: a first-ever price dated next
   * quarter is created successfully and does not appear in today's list, so a screen
   * built on `data` would show the operator nothing and read as though the save failed.
   */
  written: CountryProductDto[];
}

/** `GET /countries/:cc/products/currencies` — the country's quote currencies, home first. */
export interface CountryCurrenciesDto {
  currencies: string[];
  choice_required: boolean;
}

/**
 * The lists a country may keep (migration 66). The price form offers exactly these, and the
 * drawer shows one block per entry — so a USD list can be opened for a product that has none
 * yet. Country scope only on the server; the one source the builder reads too.
 */
export async function fetchCountryCurrencies(
  cc: CountryCode,
  signal?: AbortSignal,
): Promise<CountryCurrenciesDto> {
  const body = await apiRequest<{ data: CountryCurrenciesDto }>(
    `/countries/${cc}/products/currencies`,
    { signal },
  );
  return body.data;
}

export async function putPrices(
  cc: CountryCode,
  items: readonly PriceEntry[],
): Promise<PutPricesResult> {
  return apiRequest<PutPricesResult>(`/countries/${cc}/products`, {
    method: 'PUT',
    body: { items },
  });
}

/* ───────────────────────────────────────────────────────────── query keys ── */

export const adminCatalogueKeys = {
  /**
   * Keyed by COUNTRY since migration 22, for the reason `productGroups` records below and
   * with one extra consequence: an unkeyed `['admin','products']` would have served
   * Angola's catalogue to the India screen out of cache, and the two lists no longer even
   * describe the same rows — `code` is unique per country now, so the stale render would
   * be a different country's TP-SILVER under the same label.
   */
  products: (cc: string) => ['admin', 'products', cc] as const,
  /** The group admin's un-narrowed interleaved list — a DIFFERENT question from any
   * single country's, so it must never share a per-country key's cache entry. */
  allProducts: () => ['admin', 'products', 'all-countries'] as const,
  /**
   * The checklist's whole-catalogue read (U4). A THIRD key, not a reuse of the two
   * above: `fetchProductsForReview` returns a different SHAPE (`rows`/`complete`, not
   * a page envelope), and one cache entry serving two shapes is how a screen renders
   * `undefined` where it expected a list. Still under the `['admin','products']`
   * prefix, so the products screen's invalidation after a rename or a confirm refreshes
   * this too — which is what makes a cleared row disappear here.
   */
  productsForReview: (cc: string) => ['admin', 'products', 'review', cc] as const,
  prices: (cc: string) => ['admin', 'country-products', cc] as const,
  priceHistory: (cc: string) => ['admin', 'country-products', cc, 'history'] as const,
  currencies: (cc: string) => ['admin', 'country-products', cc, 'currencies'] as const,
  /**
   * Keyed by COUNTRY, because groups are per country. A single `['admin','product-groups']`
   * key would serve Angola's shelves to the UAE screen out of cache on the way to a
   * refetch — briefly, silently, and exactly once per navigation.
   */
  productGroups: (cc: string) => ['admin', 'product-groups', cc] as const,
};
